import { useEffect, useState } from 'react'
import { Image, Linking, StyleSheet } from 'react-native'

import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/index'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import ClockOutlined from '@zet/design-system/svg/ClockOutlined'
import { B4, B5, SH4 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import LockFilled from '@zet/svg/lockFilled'
import { UpcomingTrainingItem } from '@zet/utils/interface'
import moment from 'moment'
import { styled } from 'nativewind'

import { useEnrollToTraining } from '../../../../api'

const CardContianer = styled(YStack, 'w-44 h-52 mr-3')
const RenderEnrollCards = ({
  item,
  trainingList,
  keyIndex,
}: {
  item: UpcomingTrainingItem
  trainingList: UpcomingTrainingItem[]
  keyIndex?: number | string
}) => {
  const { isLoading, mutate } = useEnrollToTraining()
  const [isReferesh, setIsReferesh] = useState(false)
  const hanldeButtonClick = (clickedId: string) => {
    const checkedIndex = trainingList.findIndex(({ id }) => id === clickedId)
    if (!trainingList[checkedIndex].isEnrolled) {
      mutate({
        webinarId: trainingList[checkedIndex].id,
      })
      return
    }
    if (trainingList[checkedIndex].isEnrolled) {
      Linking.openURL(trainingList[checkedIndex]?.link || 'https://onecode.in')
      return
    }
  }

  useEffect(() => {
    const refereshInterval = setInterval(() => {
      setIsReferesh(!isReferesh)
    }, 1000 * 30)
    return () => clearInterval(refereshInterval)
  }, [isReferesh])

  const hourMinute = (unxiTime: number) => {
    const resolution = unxiTime - moment(new Date()).unix()
    return {
      hours: Number((resolution / 3600).toFixed(1)),
      minutes: Number((resolution / 60).toFixed(0)),
    }
  }

  const getButtonTitle = (status: string, startTime: number, title: string) => {
    const { hours, minutes } = hourMinute(startTime)

    if (status === 'REGISTERED') {
      if (minutes > 60)
        return i18n.t('trainingCard.startsIn', {
          hours: hours.toFixed(0),
        })
      else if (minutes > 15)
        return i18n.t('trainingCard.startsInMins', { minutes: minutes })
      else return i18n.t('trainingCard.joinNow')
    } else return i18n.t('trainingCard.enrollNow')
  }

  const isLive = (startTime: number) => {
    const { minutes } = hourMinute(startTime)
    return minutes > 15 ? false : true
  }

  const getSubtitle = (startTime: number) => {
    const getStartTimeFormat = moment.unix(startTime)
    return `${getStartTimeFormat.format('Do')} ${getStartTimeFormat.format(
      'MMM'
    )}, ${getStartTimeFormat.format('ddd')} `
  }

  const { imageURI, title, isEnrolled, id, startTime, status, isBlocked } = item
  const isTrainingLive = status === 'UNREGISTERED' ? false : isLive(startTime)
  const date = getSubtitle(startTime)
  const buttonTitle = getButtonTitle(status, startTime, title)

  return (
    <CardContianer>
      <Stack className="flex-2">
        <Image
          className="rounded-md"
          style={styles.imageStyle}
          source={
            imageURI
              ? { uri: imageURI }
              : require('@zet/common/assets/images/training-placeholder.png')
          }
        />
        {isEnrolled && (
          <Stack className="relative">
            <XStack
              className="absolute items-center rounded-bl bg-black px-2 py-1"
              style={styles.bottomGap}
            >
              {isTrainingLive && (
                <Stack
                  className="bg-decorative-liveColor mr-2 rounded-full"
                  style={styles.liveDot}
                ></Stack>
              )}
              <Stack>
                <B5 className="text-white">
                  {isEnrolled && !isTrainingLive ? 'Enrolled' : 'Live'}
                </B5>
              </Stack>
            </XStack>
          </Stack>
        )}
        {isBlocked && (
          <Stack
            className="absolute right-[15] top-[5] h-6 w-6 items-center justify-center rounded border"
            style={{ backgroundColor: zetColors.tertiary[100] }}
          >
            <LockFilled />
          </Stack>
        )}
      </Stack>
      <Stack className="flex-1">
        <SH4 className="mt-2" numberOfLines={1}>
          {title}
        </SH4>
        <XStack className="mt-2 items-center">
          <ClockOutlined />
          <B5 className="ml-1">{date}</B5>
        </XStack>
        {isBlocked ? (
          <></>
        ) : isEnrolled && buttonTitle !== i18n.t('trainingCard.joinNow') ? (
          <B4 className="mt-3">{buttonTitle}</B4>
        ) : (
          <Button
            intent={isTrainingLive ? 'success' : 'primary'}
            className={
              isTrainingLive
                ? 'border-success-100 active:bg-success-100 mt-2 active:border-white'
                : 'border-primary-100 active:bg-primary-100 mt-2 active:border-white'
            }
            outlined={true}
            textStyle={
              isTrainingLive
                ? 'text-success-100 active:text-white'
                : 'text-primary-100 active:text-white'
            }
            onPress={() => hanldeButtonClick(id)}
            eventName="click_action"
            eventPayload={{
              item_name:
                buttonTitle === i18n.t('trainingCard.joinNow')
                  ? 'home_page_trainings_join'
                  : 'home_page_trainings_enroll',
              training_name: title,
            }}
            loading={isLoading}
          >
            {buttonTitle}
          </Button>
        )}
      </Stack>
    </CardContianer>
  )
}

export default RenderEnrollCards

const styles = StyleSheet.create({
  imageStyle: {
    width: '95%',
    height: 98,
  },
  liveDot: {
    width: 10,
    height: 10,
  },
  bottomGap: { bottom: 1.5 },
})
